// Dynamic parameter form driven by the report's `params` schema.
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { paramVisible } from "./reportsApi";

const BLANK = "__blank__";

function SelectField({ p, value, onChange }) {
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    if (!p.options_from) return;
    api.get(`/report-hub/options/${p.options_from}`).then((r) => setRemote(r.data.options || [])).catch(() => setRemote([]));
  }, [p.options_from]);
  const opts = p.options_from ? (remote || []) : (p.options || []);
  const hasBlank = p.options_from || opts.some((o) => o.value === "");
  return (
    <Select value={value === "" || value === undefined ? BLANK : String(value)} onValueChange={(v) => onChange(v === BLANK ? "" : v)}>
      <SelectTrigger className="h-9" data-testid={`report-param-${p.key}`}><SelectValue placeholder={remote === null && p.options_from ? "Loading…" : "Choose…"} /></SelectTrigger>
      <SelectContent className="max-h-72">
        {hasBlank && <SelectItem value={BLANK}>{(opts.find((o) => o.value === "") || {}).label || "— Choose —"}</SelectItem>}
        {opts.filter((o) => o.value !== "").map((o) => (
          <SelectItem key={o.value} value={String(o.value)} data-testid={`report-param-${p.key}-opt-${o.value}`}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function ParamFields({ report, params, onChange, compact = false }) {
  const fields = (report?.params || []).filter((p) => paramVisible(p, params));
  if (!fields.length) return <p className="text-sm text-slate-500" data-testid="report-no-params">This report takes no parameters.</p>;
  const set = (k, v) => onChange({ ...params, [k]: v });
  return (
    <div className={`grid gap-3 ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`} data-testid="report-params">
      {fields.map((p) => (
        <div key={p.key} className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {p.label}{p.required && <span className="text-rose-500"> *</span>}
          </Label>
          {p.type === "select" ? (
            <SelectField p={p} value={params[p.key]} onChange={(v) => set(p.key, v)} />
          ) : p.type === "bool" ? (
            <label className="flex h-9 items-center gap-2 text-sm text-slate-700">
              <Switch checked={!!params[p.key]} onCheckedChange={(v) => set(p.key, v)} data-testid={`report-param-${p.key}`} />
              {params[p.key] ? "Yes" : "No"}
            </label>
          ) : (
            <Input type={p.type === "month" ? "month" : p.type === "date" ? "date" : "text"} className="h-9"
                   value={params[p.key] ?? ""} onChange={(e) => set(p.key, e.target.value)}
                   data-testid={`report-param-${p.key}`} />
          )}
          {p.help && <p className="text-[11px] text-slate-400">{p.help}</p>}
        </div>
      ))}
    </div>
  );
}
